import { z } from "zod";
import { PLAN_IDS, type PlanId } from "./billing/subscription.js";

/** Config ระบบที่ Admin ปรับได้ (เก็บใน DynamoDB ไม่ต้อง deploy ใหม่) */
export const systemConfigSchema = z.object({
  ai_enabled: z.boolean(),
  /** เพดาน token รายวันทั้งระบบ 0 = ไม่จำกัด เมื่อถึงเพดานระบบปิดการประเมินอัตโนมัติและแจ้ง Admin */
  daily_token_cap: z.number().int().min(0).max(10_000_000_000),
  /** ค่าดำเนินการ/กำไรตั้งต้น (%) */
  default_overhead_pct: z.number().min(0).max(100),
  /** ราคา AI ต่อ 1 ล้าน token (บาท) สำหรับรายงานต้นทุน ว่าง = ยังไม่ตั้งค่า (รายงานแสดงเฉพาะ token) */
  ai_price_per_1m_input_thb: z.number().min(0).max(1_000_000).nullable(),
  ai_price_per_1m_output_thb: z.number().min(0).max(1_000_000).nullable(),
  /** ข้อมูลผู้ให้บริการ (ผู้ควบคุมข้อมูลตาม PDPA) แสดงในนโยบายความเป็นส่วนตัวและใบเสร็จ ว่าง = ยังไม่ตั้งค่า */
  legal_name: z.string().trim().max(200).nullable(),
  legal_address: z.string().trim().max(500).nullable(),
  legal_tax_id: z.string().trim().max(13).nullable(),
  legal_phone: z.string().trim().max(40).nullable(),
  support_email: z.string().trim().email().max(120).nullable(),
  support_email_2: z.string().trim().email().max(120).nullable(),
});
export type SystemConfig = z.infer<typeof systemConfigSchema>;

export const DEFAULT_SYSTEM_CONFIG: SystemConfig = {
  ai_enabled: true,
  daily_token_cap: 0,
  default_overhead_pct: 20,
  ai_price_per_1m_input_thb: null,
  ai_price_per_1m_output_thb: null,
  legal_name: null,
  legal_address: null,
  legal_tax_id: null,
  legal_phone: null,
  // อีเมลติดต่อ/ร้องเรียนของผู้ให้บริการ (ตามที่เจ้าของระบบระบุ) Admin แก้ได้ที่หน้าตั้งค่าระบบ
  support_email: "aiinnovation.th@gmail.com",
  support_email_2: "theheronat@gmail.com",
};

export const planLimitsUpdateSchema = z.object({
  price_thb: z.number().min(0).max(1_000_000),
  estimates_per_month: z.number().int().min(0).max(1_000_000),
  saved_jobs_limit: z.number().int().min(0).max(1_000_000).nullable(),
  quotation: z.boolean(),
  gate_per_day: z.number().int().min(0).max(1_000_000),
  ai_tokens_per_month: z.number().int().min(0).max(100_000_000_000),
});
export type PlanLimitsUpdate = z.infer<typeof planLimitsUpdateSchema>;
export const planIdParam = z.enum(PLAN_IDS);

export const priceUpdateSchema = z
  .object({
    labor_min_thb: z.number().min(0).max(10_000_000),
    labor_max_thb: z.number().min(0).max(10_000_000),
    material_min_thb: z.number().min(0).max(10_000_000),
    material_max_thb: z.number().min(0).max(10_000_000),
    source_id: z.string().min(1).max(80),
    note: z.string().max(300).optional(),
  })
  .refine((v) => v.labor_min_thb <= v.labor_max_thb && v.material_min_thb <= v.material_max_thb, "min_gt_max");
export type PriceUpdate = z.infer<typeof priceUpdateSchema>;

export const priceSourceSchema = z.object({
  name: z.string().min(1).max(200),
  kind: z.enum(["internal_reference", "supplier", "official_wage", "price_index", "web", "other"]),
  kind_label_th: z.string().min(1).max(80),
  url: z.string().url().max(500).or(z.literal("")),
  effective_from: z.string().date(),
});
export type PriceSourceInput = z.infer<typeof priceSourceSchema>;

/** Admin กำหนดแพ็กเกจให้ผู้ใช้เอง (Manual Assign) : mode "normal" ใช้กติกาเดียวกับการชำระเงินปกติ, "replace" ตั้งแพ็กเกจใหม่ทันที 30 วัน (ใช้แก้ไข/ลดแพ็กเกจ) */
export const assignPlanRequestSchema = z.object({
  plan: z.enum(PLAN_IDS),
  mode: z.enum(["normal", "replace"]).default("normal"),
  /** เหตุผล บังคับ เก็บใน audit และประวัติของผู้ใช้ */
  note: z.string().trim().min(3).max(300),
});
export type AssignPlanRequest = z.infer<typeof assignPlanRequestSchema>;

export interface AdminUserRow {
  userId: string;
  email: string;
  name: string | null;
  createdAt: string;
  plan: PlanId;
  planExpiresAt: string | null;
  savedJobs: number;
  estimatesUsed: number;
  estimatesLimit: number;
}
